/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router";

import { useAuth } from "Authentication/AuthProvider";
import { AddComment } from "Components/AddComment/AddComment";
import { CommentsHistory } from "Components/CommentsHistory/CommentsHistory";
import { OperationStatus } from "Constants/OperationStatus";
import { PermissionsEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { DetailWrapper } from "Containers/Common/CommonContainer.styles";
import { addNewComment, loadUserProfileComments } from "Dataflows/UserProfile/UserProfileAction";
import {
  getAddCommentOperationStatus,
  getAppStateUserProfile,
  getAppStateUserProfileComments,
  getLoadCommentsError,
  isUserProfileCommentsLoading,
} from "Dataflows/UserProfile/UserProfileSelector";

/**
 * Student comments
 * @Component
 * @returns {React.Component<>}
 */
const StudentComments = (): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles, account, hasPermission } = useAuth();

  const userProfileId = useSelector(getAppStateUserProfile)?.id;
  const loadCommentsError = useSelector(getLoadCommentsError);
  const isLoading = useSelector(isUserProfileCommentsLoading);
  const userProfileComments = useSelector(getAppStateUserProfileComments);
  const addCommentOpStatus = useSelector(getAddCommentOperationStatus);
  const dispatch = useDispatch();

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  useEffect(() => {
    if (!!userProfileId) {
      dispatch(loadUserProfileComments(userProfileId));
    }
  }, [addCommentOpStatus === OperationStatus.Succeeded]);

  const onAddCommentClicked = (newComment): void => {
    if (!!userProfileId && !!newComment && !!account.userName) {
      dispatch(addNewComment(userProfileId, newComment, account.userName));
    }
  };

  const canAddComments = hasPermission(TenantTypesEnum.MSP, PermissionsEnum.MSA_UPDATE_USERS);

  return (
    <DetailWrapper>
      {canAddComments && (
        <AddComment
          onAddCommentClicked={onAddCommentClicked}
          addCommentStatus={addCommentOpStatus}
        />
      )}
      <CommentsHistory
        userProfileComments={userProfileComments}
        isLoading={isLoading}
        errorMessage={loadCommentsError}
      />
    </DetailWrapper>
  );
};

export default StudentComments;
